import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';

export interface TeamContractRules {
  requireAditivosForSuspendedLines: boolean;
  requireSeraUsadaPeloCliente: boolean;
  requirePortabilidadeCompleta: boolean;
  blockDuplicateCcid: boolean;
  blockDuplicatePhone: boolean;
  requireClienteAtivo: boolean;
  allowContractDraft: boolean;
  allowContractLineEdit: boolean;
  /** Preferência de cluster Fastchip RJ/MG/ES do time (null = sem preferência, usa o padrão do admin). */
  rjMgEsFastchipClusterPreference?: 'ER2' | 'DH' | null;
}

export const defaultContractRules: TeamContractRules = {
  requireAditivosForSuspendedLines: true,
  requireSeraUsadaPeloCliente: true,
  requirePortabilidadeCompleta: true,
  blockDuplicateCcid: true,
  blockDuplicatePhone: true,
  requireClienteAtivo: true,
  allowContractDraft: true,
  allowContractLineEdit: true,
  rjMgEsFastchipClusterPreference: null,
};

export type TeamKind = 'Sales' | 'Cqv' | 'PosVenda';

export function isCqvKind(kind: unknown): boolean {
  if (kind === 1 || kind === '1') return true;
  if (typeof kind !== 'string' && typeof kind !== 'number') return false;
  return String(kind).toLowerCase() === 'cqv';
}

export function isPosVendaKind(kind: unknown): boolean {
  if (kind === 2 || kind === '2') return true;
  if (typeof kind !== 'string' && typeof kind !== 'number') return false;
  return String(kind).toLowerCase() === 'posvenda';
}

export function normalizeTeamKind(kind: unknown): TeamKind {
  if (isCqvKind(kind)) return 'Cqv';
  if (isPosVendaKind(kind)) return 'PosVenda';
  return 'Sales';
}

export const DAVI_CQV_EMAIL = 'davi.custodio@getteltelecom.com';

export function canMutateCqvTeam(actorEmail: string | undefined, actorUserId: string | undefined, managerId: string | undefined): boolean {
  const email = (actorEmail || '').trim();
  if (email.toLowerCase() === DAVI_CQV_EMAIL) return true;
  return !!actorUserId && !!managerId && actorUserId === managerId;
}

export interface TeamMemberVtmeFlagDto {
  userId: string;
  receivesVtmeEmail: boolean;
}

export interface TeamResponseDto {
  id: string;
  name: string;
  managerId: string;
  managerName: string;
  managerEmail: string;
  salespersons: TeamMemberDto[];
  salespersonsCount: number;
  isTrainingTeam: boolean;
  kind: TeamKind;
  contractRules: TeamContractRules;
  createdAt: string;
  updatedAt: string;
}

export interface TeamListDto {
  id: string;
  name: string;
  managerId: string;
  managerName: string;
  managerUsuarioVtme?: string | null;
  /** Presente nas respostas atuais da API; fallback para salespersonsIds se ausente. */
  salespersons?: TeamMemberDto[];
  salespersonsCount: number;
  salespersonsNames: string[];
  salespersonsIds: string[];
  isTrainingTeam: boolean;
  kind?: TeamKind;
  contractRules: TeamContractRules;
  createdAt: string;
}

export interface CreateTeamDto {
  name: string;
  managerId: string;
  salespersonIds: string[];
  isTrainingTeam: boolean;
  kind: TeamKind;
  memberVtmeFlags?: TeamMemberVtmeFlagDto[];
}

export interface UpdateTeamDto {
  name: string;
  managerId: string;
  salespersonIds: string[];
  isTrainingTeam: boolean;
  kind: TeamKind;
  memberVtmeFlags?: TeamMemberVtmeFlagDto[];
}

export interface TeamMemberDto {
  userId: string;
  userName: string;
  userEmail: string;
  isActive: boolean;
  usuarioVtme?: string | null;
  receivesVtmeEmail?: boolean;
}

export interface AddMemberDto {
  userId: string;
}

export interface TeamStatsDto {
  teamId: string;
  teamName: string;
  totalContracts: number;
  activeContracts: number;
  totalMonthlyValue: number;
  contractsToday: number;
  conversionRate: number;
}

/**
 * A lista de times muda raramente (criar/editar time é uma ação administrativa esporádica), mas era
 * buscada várias vezes por sessão: página de contratos, CQV, campanhas, treinamentos e relatórios,
 * cada uma a pedir `/teams` no seu próprio mount. Em produção isso resultou em dezenas de consultas
 * idênticas simultâneas no Postgres.
 *
 * O TTL segura releituras próximas e o `inFlight` coalesce chamadas concorrentes numa só requisição.
 * Qualquer mutação de time limpa o cache, portanto o admin nunca vê a lista desatualizada.
 *
 * Os consumidores React usam `useTeamsQuery` / `fetchQuery` com o mesmo staleTime e este `getTeams`
 * como queryFn — um único caminho HTTP, sem segundo cache independente.
 */
const TEAMS_CACHE_TTL_MS = 5 * 60 * 1000;

export class TeamService extends BaseApiService {
  private teamsCache: { data: TeamListDto[]; expiresAt: number } | null = null;
  private teamsInFlight: Promise<TeamListDto[]> | null = null;

  // ===== MÉTODOS BÁSICOS DE TIMES =====

  /** Invalida o cache da lista de times. Chamado após qualquer mutação. */
  invalidateTeamsCache(): void {
    this.teamsCache = null;
    this.teamsInFlight = null;
  }

  // Buscar todos os times (com cache de curta duração e dedup de chamadas concorrentes)
  async getTeams(options?: { forceRefresh?: boolean }): Promise<TeamListDto[]> {
    if (options?.forceRefresh) {
      this.invalidateTeamsCache();
    }

    const cached = this.teamsCache;
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }

    if (this.teamsInFlight) {
      return this.teamsInFlight;
    }

    this.teamsInFlight = (async () => {
      try {
        const response = await this.api.get<ApiResponse<TeamListDto[]>>('/teams');
        const data = this.extractData(response);
        this.teamsCache = { data, expiresAt: Date.now() + TEAMS_CACHE_TTL_MS };
        return data;
      } finally {
        this.teamsInFlight = null;
      }
    })();

    return this.teamsInFlight;
  }

  /**
   * Regras de contrato do usuário autenticado.
   *
   * Consulta leve: a API resolve a linha do próprio usuário. Antes o frontend carregava
   * `/teams` inteiro (com managers e vendedores de todos os times) só para filtrar em memória.
   */
  async getMyContractRules(): Promise<TeamContractRules> {
    const response = await this.api.get<ApiResponse<TeamContractRules> | TeamContractRules>(
      '/teams/my-contract-rules'
    );
    return { ...defaultContractRules, ...this.extractData(response) };
  }

  // Buscar apenas times de treinamento
  async getTrainingTeams(): Promise<TeamListDto[]> {
    const teams = await this.getTeams();
    return teams.filter(team => team.isTrainingTeam);
  }

  // Buscar time por ID
  async getTeam(id: string): Promise<TeamResponseDto> {
    const response = await this.api.get<ApiResponse<TeamResponseDto>>(`/teams/${id}`);
    return this.extractData(response);
  }

  // Criar time
  async createTeam(data: CreateTeamDto): Promise<TeamResponseDto> {
    const response = await this.api.post<ApiResponse<TeamResponseDto>>('/teams', data);
    this.invalidateTeamsCache();
    return this.extractData(response);
  }

  // Atualizar time
  async updateTeam(id: string, data: UpdateTeamDto): Promise<TeamResponseDto> {
    const response = await this.api.put<ApiResponse<TeamResponseDto>>(`/teams/${id}`, data);
    this.invalidateTeamsCache();
    return this.extractData(response);
  }

  // Deletar time
  async deleteTeam(id: string): Promise<void> {
    await this.api.delete(`/teams/${id}`);
    this.invalidateTeamsCache();
  }

  // ===== MÉTODOS DE MEMBROS =====

  // Adicionar membro ao time
  async addMember(teamId: string, data: AddMemberDto): Promise<void> {
    await this.api.post(`/teams/${teamId}/members`, data);
    this.invalidateTeamsCache();
  }

  // Remover membro do time
  async removeMember(teamId: string, userId: string): Promise<void> {
    await this.api.delete(`/teams/${teamId}/members/${userId}`);
    this.invalidateTeamsCache();
  }

  // Atualizar regras de contrato do time
  async updateContractRules(teamId: string, rules: TeamContractRules): Promise<TeamContractRules> {
    const response = await this.api.put<ApiResponse<TeamContractRules>>(`/teams/${teamId}/contract-rules`, rules);
    this.invalidateTeamsCache();
    return this.extractData(response);
  }

  // ===== MÉTODOS ESTATÍSTICOS =====

  // Buscar estatísticas dos times
  async getTeamStats(): Promise<TeamStatsDto[]> {
    const response = await this.api.get<ApiResponse<TeamStatsDto[]>>('/teams/stats');
    return this.extractData(response);
  }

  // ===== MÉTODOS UTILITÁRIOS =====

  // Verificar se um time existe
  async teamExists(id: string): Promise<boolean> {
    try {
      await this.getTeam(id);
      return true;
    } catch {
      return false;
    }
  }

  // Verificar se um nome de time já existe
  async teamNameExists(name: string, excludeId?: string): Promise<boolean> {
    try {
      const teams = await this.getTeams();
      const existingTeam = teams.find(team => team.name.toLowerCase() === name.toLowerCase());
      return excludeId ? existingTeam?.id !== excludeId : !!existingTeam;
    } catch {
      return false;
    }
  }

  // Obter times para dropdown/select
  async getTeamsForDropdown(): Promise<Array<{ id: string; name: string; managerName: string }>> {
    const teams = await this.getTeams();
    return teams.map(team => ({
      id: team.id,
      name: team.name,
      managerName: team.managerName
    }));
  }

  // Obter membros de um time para dropdown/select
  async getTeamMembersForDropdown(teamId: string): Promise<Array<{ userId: string; userName: string; userEmail: string }>> {
    const team = await this.getTeam(teamId);
    return team.salespersons.map(member => ({
      userId: member.userId,
      userName: member.userName,
      userEmail: member.userEmail
    }));
  }

  // Validar dados de criação de time
  validateTeamData(teamData: CreateTeamDto): string[] {
    const errors: string[] = [];

    if (!teamData.name || teamData.name.trim().length < 2) {
      errors.push('Nome do time deve ter pelo menos 2 caracteres');
    }

    if (!teamData.managerId || teamData.managerId.trim().length === 0) {
      errors.push('Manager é obrigatório');
    }

    if (!teamData.salespersonIds || teamData.salespersonIds.length === 0) {
      errors.push(isCqvKind(teamData.kind)
        ? 'Selecione pelo menos um membro do time CQV'
        : 'Pelo menos um vendedor deve ser selecionado');
    }

    return errors;
  }

  // Validar dados de atualização de time
  validateUpdateTeamData(teamData: UpdateTeamDto): string[] {
    return this.validateTeamData(teamData);
  }

  // Validar dados de membro
  validateMemberData(memberData: AddMemberDto): string[] {
    const errors: string[] = [];

    if (!memberData.userId || memberData.userId.trim().length === 0) {
      errors.push('ID do usuário é obrigatório');
    }

    return errors;
  }
}

// Instância singleton
export const teamService = new TeamService();
export default teamService;
